// ThreadActions — resolve + assign for a thread (Epic C3).
//
// A thread is a QUEUE item — the ONLY conversation object that is assignable and
// resolvable. fn_thread_assign / fn_thread_resolve refuse on a channel or a DM
// ("a DM has no owner", "channels and DMs are not resolvable"), so this component
// is rendered only by <ThreadView> (the one pane for a thread). Resolve toggles
// fn_thread_resolve; Assign opens a member-select modal that writes through
// fn_thread_assign. Both are writes — never retried. Errors surface inline via
// <ErrorState> (knowledge-base §18: a failed write is a real error, not a silent
// empty). The member list comes from getAssignableMembers (api/conversations.js),
// fetched LAZILY when the modal opens — so a missing/unconfirmed member source
// degrades inside the picker, never breaking the page.
//
// The client-facing WALL is rendered by <ThreadView> around <Composer>, not here
// — this component owns only the resolve/assign controls. The p_client_facing
// toggle inside <Composer> itself is a kit gap flagged for Charles (never fork).
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  assignThread,
  resolveThread,
  getAssignableMembers,
} from "../../../api/conversations";
import { ErrorState } from "../../shared/StateMessage";

/**
 * @param {object} props
 * @param {object} props.thread   { id, resolved, client_facing, assigned_to, assigned }
 * @param {(threadId: string, resolved: boolean) => void} [props.onResolved]
 *   Notified after a successful resolve/reopen so the parent can sync the rail.
 * @param {(threadId: string, member: { id: string, name: string }) => void} [props.onAssigned]
 *   Notified after a successful assign so the parent can sync the rail.
 */
export default function ThreadActions({ thread, onResolved, onAssigned }) {
  const t = thread || {};
  const [resolved, setResolved] = useState(!!t.resolved);
  const [resolving, setResolving] = useState(false);
  const [resolveError, setResolveError] = useState(null);

  const [assignee, setAssignee] = useState(
    t.assigned_to ? { id: t.assigned_to, name: t.assigned } : null,
  );
  const [modalOpen, setModalOpen] = useState(false);
  const [members, setMembers] = useState([]);
  const [membersStatus, setMembersStatus] = useState("idle"); // idle|loading|ready|error
  const [membersError, setMembersError] = useState(null);
  const [assigning, setAssigning] = useState(false);
  const [assignError, setAssignError] = useState(null);
  const panelRef = useRef(null);

  const handleResolve = useCallback(async () => {
    if (resolving) return;
    setResolving(true);
    setResolveError(null);
    try {
      const next = !resolved;
      await resolveThread(t.id, next);
      setResolved(next);
      onResolved?.(t.id, next);
    } catch (err) {
      setResolveError(err?.message || "Couldn't update this thread.");
    } finally {
      setResolving(false);
    }
  }, [t.id, resolved, resolving, onResolved]);

  const loadMembers = useCallback(async () => {
    setMembersStatus("loading");
    setMembersError(null);
    try {
      const rows = await getAssignableMembers();
      setMembers(Array.isArray(rows) ? rows : []);
      setMembersStatus("ready");
    } catch (err) {
      setMembersError(err?.message || "Couldn't load the team.");
      setMembersStatus("error");
    }
  }, []);

  const openAssign = useCallback(() => {
    setAssignError(null);
    setModalOpen(true);
    if (membersStatus === "idle") loadMembers();
  }, [membersStatus, loadMembers]);

  const closeAssign = useCallback(() => {
    if (assigning) return;
    setModalOpen(false);
    setAssignError(null);
  }, [assigning]);

  const handlePick = useCallback(
    async (member) => {
      if (assigning) return;
      setAssigning(true);
      setAssignError(null);
      try {
        await assignThread(t.id, member.id);
        const next = { id: member.id, name: member.name };
        setAssignee(next);
        onAssigned?.(t.id, next);
        setModalOpen(false);
      } catch (err) {
        setAssignError(err?.message || "Couldn't assign this thread.");
      } finally {
        setAssigning(false);
      }
    },
    [t.id, assigning, onAssigned],
  );

  // Esc closes the modal; move focus into the panel on open.
  useEffect(() => {
    if (!modalOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeAssign();
    };
    document.addEventListener("keydown", onKey);
    panelRef.current?.focus();
    return () => document.removeEventListener("keydown", onKey);
  }, [modalOpen, closeAssign]);

  return (
    <ThreadActionsBody
      resolved={resolved}
      resolving={resolving}
      resolveError={resolveError}
      assignee={assignee}
      assigning={assigning}
      modalOpen={modalOpen}
      membersStatus={membersStatus}
      members={members}
      membersError={membersError}
      assignError={assignError}
      panelRef={panelRef}
      onResolve={handleResolve}
      onOpenAssign={openAssign}
      onCloseAssign={closeAssign}
      onPick={handlePick}
      onRetryMembers={loadMembers}
    />
  );
}
// ── Presentational body (split out so the logic above stays readable) ─────────
function ThreadActionsBody({
  resolved,
  resolving,
  resolveError,
  assignee,
  assigning,
  modalOpen,
  membersStatus,
  members,
  membersError,
  assignError,
  panelRef,
  onResolve,
  onOpenAssign,
  onCloseAssign,
  onPick,
  onRetryMembers,
}) {
  return (
    <>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onResolve}
          disabled={resolving}
          className="text-sm font-medium text-white bg-[#32B1FF] px-3 py-1 rounded-md hover:bg-[#1a9aef] disabled:opacity-40"
          data-testid="thread-resolve"
        >
          {resolving ? "…" : resolved ? "Reopen" : "Resolve"}
        </button>
        <button
          type="button"
          onClick={onOpenAssign}
          disabled={assigning}
          className="text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white px-3 py-1 rounded-md border border-gray-200 dark:border-gray-700 disabled:opacity-40"
          data-testid="thread-assign"
        >
          {assignee ? `Assigned · ${assignee.name}` : "Assign"}
        </button>
      </div>

      {resolveError && (
        <div className="mt-2">
          <ErrorState variant="strip" message={resolveError} />
        </div>
      )}

      {modalOpen && (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Assign thread"
          data-testid="assign-modal"
          onClick={onCloseAssign}
        >
          <div
            ref={panelRef}
            tabIndex={-1}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-sm rounded-xl border border-gray-200 bg-white p-4 shadow-xl focus:outline-none dark:border-gray-800 dark:bg-gray-900"
          >
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Assign this thread
              </h3>
              <button
                type="button"
                onClick={onCloseAssign}
                disabled={assigning}
                className="text-sm text-gray-400 hover:text-gray-900 disabled:opacity-40 dark:hover:text-white"
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            {membersStatus === "loading" && (
              <div className="space-y-2" role="status" aria-label="Loading members">
                {[0, 1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="h-9 animate-pulse rounded-md bg-gray-100 dark:bg-gray-800"
                  />
                ))}
              </div>
            )}

            {membersStatus === "error" && (
              <ErrorState variant="inset" message={membersError} onRetry={onRetryMembers} />
            )}

            {membersStatus === "ready" &&
              (members.length === 0 ? (
                <p className="py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                  No one is available to assign.
                </p>
              ) : (
                <ul
                  className="max-h-72 space-y-1 overflow-y-auto"
                  data-testid="assign-members"
                >
                  {members.map((m) => (
                    <li key={m.id}>
                      <button
                        type="button"
                        disabled={assigning}
                        onClick={() => onPick(m)}
                        className="w-full rounded-md px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-40 dark:text-gray-200 dark:hover:bg-gray-800"
                        data-testid={`assign-member-${m.id}`}
                      >
                        {m.name}
                        {assignee?.id === m.id && (
                          <span className="ml-2 text-xs text-[#32B1FF]">current</span>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              ))}

            {assignError && (
              <div className="mt-3">
                <ErrorState variant="strip" message={assignError} />
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
